import React from "react";
import { useTranslation } from "react-i18next";

import { KpiRow, KpiTile, KpiLabel, KpiValue, KpiSub, KpiBreakdown, KpiBreakdownRow } from "./dashboardStyles";
import { percent } from "./dashboardHelpers";
import { sumWaitingTasks } from "./taskHelpers";
import { SUCCESS_600, ERROR_600 } from "../../constant/colors";

/**
 * The dashboard's headline tiles. Two of them carry an always-visible
 * breakdown beneath the total:
 *   - Runs — the range's run accounting: in-flight + succeeded + failed = total.
 *   - Waiting for human — the current parked queues (consent / EMR / screenshots).
 * Consent sent is the one happy outcome, kept as its own green tile.
 */
const DashboardKpis = ({ stats }) => {
  const { t } = useTranslation();

  const totalRuns = stats?.totalRuns ?? 0;
  const distinctPatients = stats?.distinctPatients ?? 0;
  const consentSent = stats?.consentSent ?? 0;
  const declined = stats?.byOutcome?.fn_consent_sending_declined ?? 0;
  const inFlight = stats?.byOutcome?.in_flight ?? 0;
  const failedRuns = stats?.byOutcome?.failure ?? 0;
  // Everything that finished without crashing (the residual): in-flight + this
  // + failed always sums back to totalRuns.
  const succeeded = Math.max(0, totalRuns - inFlight - failedRuns);

  const waitingConsent = stats?.waiting?.consentApproval ?? 0;
  const waitingEmr = stats?.waiting?.emrFill ?? 0;
  const waitingScreenshots = stats?.waiting?.screenshotsUpload ?? 0;
  // `eeReview` rides on newer platform payloads only (MVP-31310) — older
  // payloads mid-deploy just contribute 0.
  const waitingEeReview = stats?.waiting?.eeReview ?? 0;
  const waitingTotal = sumWaitingTasks(stats?.waiting);

  const tiles = [
    {
      key: "runs",
      label: t("agentic_dashboard.kpi.runs"),
      value: totalRuns,
      sub: t("agentic_dashboard.kpi.distinct_patients_sub", { count: distinctPatients }),
      breakdown: [
        { key: "in_flight", label: t("agentic_dashboard.kpi.in_flight"), value: inFlight },
        { key: "succeeded", label: t("agentic_dashboard.kpi.succeeded"), value: succeeded, valueColor: SUCCESS_600 },
        { key: "failed", label: t("agentic_dashboard.kpi.failed"), value: failedRuns, valueColor: ERROR_600 }
      ]
    },
    {
      key: "waiting_human",
      label: t("agentic_dashboard.kpi.waiting_human"),
      value: waitingTotal,
      sub: t("agentic_dashboard.kpi.now"),
      breakdown: [
        { key: "consent", label: t("agentic_dashboard.kpi.task_consent"), value: waitingConsent },
        { key: "emr", label: t("agentic_dashboard.kpi.task_emr"), value: waitingEmr },
        { key: "screenshots", label: t("agentic_dashboard.kpi.task_screenshots"), value: waitingScreenshots },
        { key: "ee_review", label: t("agentic_dashboard.kpi.task_ee_review"), value: waitingEeReview }
      ]
    },
    {
      key: "consent_sent",
      label: t("agentic_dashboard.kpi.consent_sent"),
      value: consentSent,
      valueColor: SUCCESS_600,
      sub: t("agentic_dashboard.kpi.pct_of_runs", { pct: percent(consentSent, totalRuns) })
    },
    {
      key: "declined",
      label: t("agentic_dashboard.kpi.declined"),
      value: declined,
      valueColor: ERROR_600,
      sub: t("agentic_dashboard.kpi.pct_of_runs", { pct: percent(declined, totalRuns) })
    }
  ];

  return (
    <KpiRow data-testid="agentic-dashboard-kpis">
      {tiles.map((tile) => (
        <KpiTile key={tile.key} data-testid={`agentic-dashboard-kpi-${tile.key}`}>
          <KpiLabel>{tile.label}</KpiLabel>
          <KpiValue $color={tile.valueColor}>{tile.value.toLocaleString()}</KpiValue>
          {tile.sub && <KpiSub>{tile.sub}</KpiSub>}
          {tile.breakdown && (
            <KpiBreakdown>
              {tile.breakdown.map((row) => (
                <KpiBreakdownRow key={row.key}>
                  <span>{row.label}</span>
                  <b style={{ color: row.valueColor }}>{row.value.toLocaleString()}</b>
                </KpiBreakdownRow>
              ))}
            </KpiBreakdown>
          )}
        </KpiTile>
      ))}
    </KpiRow>
  );
};

export { DashboardKpis };
